import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { Search } from 'lucide-react';
import { cn } from '@/lib/utils';

interface FilterSearchListProps {
  title: string;
  items: Array<{ id: string; name: string; count?: number }>;
  selected: Set<string>;
  onSelectionChange: (selected: Set<string>) => void;
  placeholder?: string;
  className?: string;
}

export default function FilterSearchList({
  title,
  items,
  selected,
  onSelectionChange,
  placeholder = 'Keresés...',
  className,
}: FilterSearchListProps) {
  const { t } = useTranslation();
  const [searchQuery, setSearchQuery] = useState('');

  const filteredItems = useMemo(() => {
    if (!searchQuery.trim()) return items;
    const lowerQuery = searchQuery.toLowerCase();
    return items.filter(item => 
      item.name.toLowerCase().includes(lowerQuery)
    );
  }, [items, searchQuery]);

  const handleToggle = (itemId: string) => {
    const newSelected = new Set(selected);
    if (newSelected.has(itemId)) {
      newSelected.delete(itemId);
    } else {
      newSelected.add(itemId);
    }
    onSelectionChange(newSelected);
  };

  return (
    <div className={cn('space-y-2', className)}>
      <div className="text-sm font-medium">{title}</div>
      
      {/* Search input */}
      <div className="relative">
        <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          type="text"
          placeholder={placeholder}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-8 h-8 text-sm"
        />
      </div>

      {/* Scrollable list */}
      <div className="border rounded-md max-h-[150px] overflow-y-auto">
        {filteredItems.length === 0 ? (
          <div className="p-3 text-sm text-muted-foreground text-center">
            {t("common.noResults")}
          </div>
        ) : (
          <div className="p-1.5 space-y-0.5">
            {filteredItems.map((item) => {
              const isSelected = selected.has(item.id);
              return (
                <label
                  key={item.id}
                  className={cn(
                    'flex items-center gap-2 py-1.5 px-1.5 rounded-md cursor-pointer hover:bg-muted transition-colors min-h-[2rem]',
                    isSelected && 'bg-primary/10 text-primary'
                  )}
                >
                  <Checkbox
                    size="2xs"
                    checked={isSelected}
                    onCheckedChange={() => handleToggle(item.id)}
                  />
                  <span className="text-sm flex-1 truncate">{item.name}</span>
                  {item.count !== undefined && (
                    <span className="text-xs text-muted-foreground">
                      {item.count}
                    </span>
                  )}
                </label>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

